import React, { useContext } from "react";
import styled from "styled-components";
import { RETURN_VIEW } from "./utils";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import GeminiContext from "common/chat/GeminiContext";
import { CHATBOT_ACTION } from "common/chat/CHATBOT_ACTION";
import { useSearchParams } from "react-router-dom";

const Wrapper = styled.div`
  width: 100%;
  margin: 0 0px 30px;
`;

// The box kept while the status of the BQN is not retrieved: it reserves the
// height of the alarms so that the card does not jump when they arrive, and
// it carries no colour, since no status has been reported yet.
const Unknown = styled.div`
  width: 100%;
  height: 50px;
`;

const NoAlarm = styled.div`
  width: 100%;
  color: white;
  font-family: "AllianceNo2-SemiBold", sans-serif !important;
  display: flex;
  flex-direction: column;
  background: ${(props) => props.$background || "transparent"};
  padding: 0 8px;
  position: relative;
  height: 50px;
  display: flex;
  justify-content: center;
`;

const AlarmWrapper = styled.div`
  width: 100%;
  color: white;
  font-family: "AllianceNo2-SemiBold", sans-serif !important;
  display: flex;
  flex-direction: row;
  background: ${(props) => props.$background || "transparent"};
  padding: 0 8px;
  position: relative;
  height: ${(props) => props.$height || 50}px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid var(--chart-grid-color);
  font-size: 12px;
  line-height: 14px;

  &:hover {
    cursor: pointer;
  }
`;

const COMPACT_ROW_HEIGHT = 24;

const AssistantButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${(props) => (props.$compact ? "2px 4px" : "5px")};
  background-color: transparent;

  span {
    line-height: 11px;
    margin: 0px 3px 0px 0px;
    padding: 0px;
    font-size: 11px !important;
    cursor: pointer;
    /* Materialize offsets the text of a .btn: keep it vertically centered. */
    top: 0px !important;
    color: white;
    transition: 0.5s;
  }

  svg {
    font-size: 12px;
    color: white;
    transition: 0.5s;
  }

  &:hover {
    span {
      color: var(--title-color);
    }
    svg {
      color: var(--title-color);
    }
  }
`;

const STATUS_HEX_MAP = {
  unknown: "var(--signal-grey, #bcbcbc)",
  normal: "var(--signal-green, #00ba07)",
  notice: "var(--signal-yellow, #baa700)",
  warning: "var(--signal-yellow, #baa700)",
  critical: "#de0c0c",
};

const HEIGHT_MAP = {
  0: 50,
  1: 50,
  2: 45,
  3: 30,
  4: 28,
  5: 24,
  6: 20,
};

const ALARMS_LINK_MAP = {
  billing: { link: "viewStatusBilling", title: "Billing Sync" },
  bqnmgr: { link: "viewLicense", title: "License" },
  "license-available": { link: "viewLicense", title: "License Available" },
  "license-expiration": { link: "viewLicense", title: "License Expiration" },
  "license-usage": { link: "viewLicense", title: "License Usage" },
  cpu: { link: "viewStatusSystemCPU", title: "CPU" },
  disk: { link: "viewStatusSystemDisk", title: "Disk" },
  time: { link: "viewSysNtp", title: "NTP" },
  "traffic-inverted": { link: "viewIfaceStkStats", title: "Inverted Traffic" },
  "traffic-low": { link: "viewIfaceStkStats", title: "Low Traffic" },
  "traffic-uplink": { link: "viewIfaceStkStats", title: "Uplink Traffic" },
  "traffic-downlink": { link: "viewIfaceStkStats", title: "Downlink Traffic" },
  "memory-dpdk": { link: "viewStatusSystemMemory", title: "Memory dpdk" },
  "memory-pool": { link: "viewStatusSystemMemory", title: "Memory pool" },
  wire: { link: "viewStatusInterfacesDataWires", title: "Network Interfaces" },
};

const ORDER = {
  critical: 0,
  warning: 1,
  notice: 2,
  unknown: 3,
};

// The alarms of the BQN selected, which are the ones the view is given: the
// status of each type of alarm, indexed by type, or undefined while the
// status of the BQN is being retrieved.
function Alarms({ data }) {
  const { geminiClient, sendAction } = useContext(GeminiContext);
  const [searchParams] = useSearchParams();
  const shouldShowAIButton = searchParams.get('ai') === 'true';

  // The status of the BQN has not been retrieved yet: nothing is known of the
  // alarms, so none of them is drawn, not even the green of the ones cleared.
  if (data === undefined) {
    return (
      <Wrapper>
        <Unknown />
      </Wrapper>
    );
  }

  const alarms = Object.entries(data)
    .filter(([_type, status]) => status !== "normal" && status !== "unknown")
    .map(([type, status]) => ({ type, status }))
    .sort((a, b) => ORDER[a.status] - ORDER[b.status]);

  if (alarms.length === 0) {
    return (
      <Wrapper>
        <NoAlarm $background={STATUS_HEX_MAP["normal"]}>No Alarms</NoAlarm>
      </Wrapper>
    );
  }

  const height = alarms.length > 6 ? 18 : HEIGHT_MAP[alarms.length];
  const compact = height < COMPACT_ROW_HEIGHT;

  return (
    <Wrapper>
      {alarms.map(({ type, status }) => {
        const alarmInfo = ALARMS_LINK_MAP[type] || { link: "#", title: type };

        return (
          <AlarmWrapper
            onClick={() => {
              if (
                alarmInfo.link !== "#" &&
                typeof globalNavigate === "function"
              ) {
                globalNavigate(alarmInfo.link, { returnView: RETURN_VIEW });
              }
            }}
            $background={STATUS_HEX_MAP[status]}
            key={`alarm-${type}`}
            $height={height}
          >
            {alarmInfo.title}
            {geminiClient === undefined || !shouldShowAIButton ? null : (
              <AssistantButton
                type="button"
                className="btn waves-effect btn-link"
                title="Ask the AI Assistant about this alarm"
                $compact={compact}
                onClick={(e) => {
                  e.stopPropagation();
                  sendAction(
                    CHATBOT_ACTION.CHECK_ALARMS,
                    `Please help me fix the ${alarmInfo.title} alarm`,
                  );
                }}
              >
                {compact ? null : <span>AI</span>}
                <AutoAwesomeIcon />
              </AssistantButton>
            )}
          </AlarmWrapper>
        );
      })}
    </Wrapper>
  );
}

export default Alarms;
